import React, { useState, Fragment, useEffect } from 'react';

import { InlineIcon } from '@spark-labs/avocado-ui';
import { BubbleButton } from '@go-client/components/BubbleUI';

interface PaginationProps {
  pages: number;
  actualPage: number;
  onPageChange?: (from: number, to: number) => void;
}

const MAX_PAGES = 7;
export const Pagination: React.FC<PaginationProps> = ({ actualPage, pages, onPageChange }) => {
  const [currentPage, setCurrentPage] = useState(actualPage ?? 1);

  useEffect(() => {
    setCurrentPage(actualPage);
  }, [actualPage]);

  const updatePage = (newPage: number) => {
    onPageChange && onPageChange(currentPage, newPage);
    setCurrentPage(newPage);
  };

  const getPages = (): number[] => {
    const totalPages = pages;

    let startPage: number;
    let endPage: number;
    if (totalPages <= MAX_PAGES) {
      startPage = 1;
      endPage = totalPages;
    } else {
      const pagesBeforeCurrentPage = Math.floor(MAX_PAGES / 2);
      const pagesAfterCurrentPage = Math.ceil(MAX_PAGES / 2) - 1;
      if (currentPage <= pagesBeforeCurrentPage) {
        startPage = 1;
        endPage = MAX_PAGES;
      } else if (currentPage + pagesAfterCurrentPage >= totalPages) {
        startPage = totalPages - MAX_PAGES + 1;
        endPage = totalPages;
      } else {
        startPage = currentPage - pagesBeforeCurrentPage;
        endPage = currentPage + pagesAfterCurrentPage;
      }
    }

    const currentPages = Array.from(Array(endPage + 1 - startPage).keys()).map(i => startPage + i);

    // Make sure page 1 is always visible
    if (!currentPages.includes(1)) {
      currentPages.splice(0, 0, 1);
    }

    // Make sure last page is always visible
    if (!currentPages.includes(totalPages)) {
      currentPages.splice(endPage, 0, totalPages);
    }

    // Add dots at the beginning
    if (currentPages[1] !== 2) {
      currentPages.splice(1, 0, -1);
    }
    // Add dots at the end
    if (currentPages[currentPages.length - 2] !== currentPages[currentPages.length - 1] + 1) {
      currentPages.splice(currentPages.length - 1, 0, -1);
    }

    return currentPages;
  };

  const dots = () => {
    return (
      <BubbleButton block appearance="triangular" disabled={true} variant="smoke">
        <span className="relative inline-flex items-center">...</span>
      </BubbleButton>
    );
  };

  const pagePill = (page: number) => {
    return (
      <BubbleButton
        block
        appearance="triangular"
        variant={currentPage === page ? 'navy' : 'smoke'}
        onClick={() => updatePage(page)}
      >
        {page}
      </BubbleButton>
    );
  };

  const allPages = getPages();

  return (
    <nav aria-label="Page navigation example">
      <ul className="flex items-center h-8">
        {/*Prev button*/}
        <li className=" text-base-i">
          <BubbleButton
            block
            appearance="triangular"
            disabled={currentPage === 1}
            variant="smoke"
            onClick={() => updatePage(currentPage - 1)}
          >
            <InlineIcon name="chevron-left" className="text-xs" />
          </BubbleButton>
        </li>

        {allPages.map((page, i) => (
          <Fragment key={`pagination-${i}`}>
            {page > 0 && (
              <li className=" text-base-i">
                <Fragment>{pagePill(page)}</Fragment>
              </li>
            )}
            {page < 0 && allPages.length > MAX_PAGES && (
              <li className=" text-base-i">
                <Fragment>{dots()}</Fragment>
              </li>
            )}
          </Fragment>
        ))}

        {/*Next button*/}
        <li className="text-base-i">
          <BubbleButton
            block
            appearance="triangular"
            variant="smoke"
            disabled={currentPage === pages || pages === 1}
            onClick={() => updatePage(currentPage + 1)}
          >
            <InlineIcon name="chevron-right" className="text-sm" />
          </BubbleButton>
        </li>
      </ul>
    </nav>
  );
};
